<template>
	<v-form ref="form">
		<v-container fluid>
			<v-layout column>
				<v-flex>
					<h2>{{ getComplainType(complaintDto.complainType) }}</h2>
				</v-flex>
				<v-flex mt-3>
					<v-layout row wrap align-center fill-height>
						<v-flex mr-5 shrink>
							<cad-preview
								:articleId="complaintDto.part.articleId"
								width="100px"
								height="100px"
								:readonly="true"
							></cad-preview>
						</v-flex>
						<v-flex mr-5 shrink>
							<v-layout column>
								<v-flex> Bezeichnung: {{ complaintDto.part.description }} </v-flex>
								<v-flex> Blexonnummer: {{ complaintDto.part.articleId }} </v-flex>
								<v-flex>
									Fertigermaterial: {{ complaintDto.part.materialBezeichnung }}
								</v-flex>
								<v-flex
									v-if="complaintDto.part.powderCoatingTreatmentDto"
									style="display: flex; align-items: center; gap: 4px"
								>
									<span
										:style="{
											display: 'inline-block',
											width: '20px',
											height: '20px',
											background:
												complaintDto.part.powderCoatingTreatmentDto.powder
													.surfaceColor.hexCode + 'ff',
										}"
									></span>
									<span>
										RAL
										{{
											complaintDto.part.powderCoatingTreatmentDto.powder
												.surfaceColor.ralCode
										}}
										| ({{
											complaintDto.part.powderCoatingTreatmentDto.powder
												.surfaceColor.name
										}}) |
										{{
											getStructureText(
												complaintDto.part.powderCoatingTreatmentDto.powder
													.structure
											)
										}}
										|
										{{
											getFinishText(
												complaintDto.part.powderCoatingTreatmentDto.powder
													.finish
											)
										}}
										<span
											v-if="
												complaintDto.part.powderCoatingTreatmentDto
													.hasBaseCoating
											"
											>&nbsp;|&nbsp;vorgrundiert</span
										>
									</span>
								</v-flex>
								<v-flex v-if="complaintDto.part.zincCoating > 0">
									{{ getZincCoatingText(complaintDto.part.zincCoating) }}
								</v-flex>
								<v-flex
									v-if="
										complaintDto.part.featureDtos != null &&
										complaintDto.part.featureDtos.length > 0
									"
								>
									<span
										v-for="(feature, index) in complaintDto.part.featureDtos"
										:key="index"
									>
										{{ feature.count }} x {{ feature.friendlyName }}
										<span
											v-if="index < complaintDto.part.featureDtos.length - 1"
											>,
										</span>
									</span>
								</v-flex>
								<v-flex v-if="complaintDto.part.hasEngravings"> Gravur </v-flex>
								<v-flex
									v-if="
										complaintDto.part.weldlinesAsText &&
										complaintDto.part.weldlinesAsText.length > 0
									"
								>
									{{ complaintDto.part.weldlinesAsText }}
								</v-flex>
							</v-layout>
						</v-flex>
						<v-flex mr-5 shrink>
							<v-layout column>
								<v-flex>
									L x B x H: {{ complaintDto.part.laenge | formatDigit }} x
									{{ complaintDto.part.breite | formatDigit }} x
									{{ complaintDto.part.hoehe | formatDigit }} mm
								</v-flex>
								<v-flex>
									Gewicht pro Teil / Gesamtgewicht der fehlerhaften Teile:
									{{ complaintDto.part.gewicht | formatDigit }} /
									{{
										(complaintDto.part.gewicht *
											complaintDto.numberOfWrongParts)
											| formatDigit
									}}
									kg
								</v-flex>
								<v-flex> Bestellmenge: {{ complaintDto.part.menge }} </v-flex>
								<v-flex>
									Anzahl Fehlerhaft: {{ complaintDto.numberOfWrongParts }}
								</v-flex>
							</v-layout>
						</v-flex>
					</v-layout>
				</v-flex>
				<v-flex mt-5>
					<v-layout row wrap>
						<v-img
							contain
							width="200px"
							height="200px"
							v-for="file in complaintDto.files"
							:key="file.id"
							lazy-src="spinner.gif"
							:src="getComplaintImageUrl(file.guid)"
							@click="showImage(file.guid)"
							title="Zum Vergrössern klicken"
							style="cursor: pointer"
						></v-img>
					</v-layout>
				</v-flex>
				<v-flex mt-3>
					<v-layout row wrap>
						<v-flex class="mr-5">
							<v-textarea
								readonly
								v-model="complaintDto.complainDescription"
								label="Beanstandung des Kunden"
							></v-textarea>
						</v-flex>
						<v-flex>
							<v-textarea
								readonly
								v-model="complaintDto.expectation"
								label="Erwartung des Kunden"
							></v-textarea>
						</v-flex>
					</v-layout>
				</v-flex>
				<v-flex>
					<h2>Lösungsvorschlag</h2>
				</v-flex>
				<v-flex>
					<v-radio-group
						:disabled="readonly"
						v-model="complaintDto.fertigerComplaintResponse"
						@change="save()"
					>
						<v-layout row wrap>
							<v-flex
								v-for="resolutionType in fertigerResponseTypes"
								:key="resolutionType.value"
							>
								<v-radio
									:label="resolutionType.name"
									:value="resolutionType.value"
								></v-radio>
							</v-flex>
						</v-layout>
					</v-radio-group>
				</v-flex>
				<v-flex v-if="complaintDto.fertigerComplaintResponse == 1">
					<v-textarea
						:disabled="readonly"
						@change="save()"
						v-model="complaintDto.fertigerDeclineExplanation"
						label="Begründung für die Ablehnung"
					></v-textarea>
				</v-flex>
			</v-layout>
		</v-container>
		<v-dialog v-model="showImageDialog" v-if="showImageDialog">
			<v-img
				contain
				width="100%"
				height="100vh"
				:src="getComplaintImageUrl(showImageGuid)"
				@click="showImageDialog = false"
			></v-img>
		</v-dialog>
	</v-form>
</template>

<script>
import httpClient from "../store/httpClient";
import complainTypes from "../dtos/complainTypes";
import CadPreview from "./cadPreviewComponent.vue";
import { getZincCoatingText } from "@/dtos/zincCoating.js";

export default {
	components: {
		"cad-preview": CadPreview,
	},
	props: {
		complaintDto: null,
		readonly: false,
	},
	data() {
		return {
			fertigerResponseTypes: [
				{
					name: "Noch nicht definiert",
					value: 0,
				},
				{
					name: "Beanstandung ablehnen",
					value: 1,
				},
				{
					name: "Ersatzlieferung",
					value: 2,
				},
				{
					name: "Nacharbeit",
					value: 3,
				},
				{
					name: "Gutschrift",
					value: 4,
				},
			],
			showImageDialog: false,
			showImageGuid: "",
		};
	},

	methods: {
		showImage(guid) {
			this.showImageGuid = guid;
			this.showImageDialog = true;
		},
		getComplainType(value) {
			return complainTypes[value].text;
		},
		getComplaintImageUrl(guid) {
			const uri = process.env.VUE_APP_helpdeskServerUrl + "api/File/GetImage?guid=" + guid;
			return uri;
		},
		async save() {
			try {
				var response = await httpClient().post(`api/ticket/UpdateManufacturerResponse`, {
					complainId: this.complaintDto.id,
					responseType: this.complaintDto.fertigerComplaintResponse,
					comment: this.complaintDto.fertigerDeclineExplanation,
					discount: 0,
				});
				this.ticketDto = response.data;
			} catch {
				console.error("Save complaint failed");
			}
		},
		getZincCoatingText(zincCoating) {
			return getZincCoatingText(zincCoating);
		},

		getFinishText(value) {
			return this.$store.getters.getPowdercoatingFinishText(value);
		},

		getStructureText(value) {
			return this.$store.getters.getPowdercoatingStructureText(value);
		},
	},
};
</script>
